/** Create / edit dialog for a tag name alias (react-hook-form + zod).
 *
 *  An alias maps a tag NAME — or a name prefix — onto a canonical token inside
 *  one category, so a condition written once as `reseller:~viator` matches every
 *  spelling a tenant carries. `tenant` is clearable: cleared = a system-wide
 *  alias (`tour_operator: null`); a tenant row shadows the system row with the
 *  same (category, alias). No optimistic-concurrency token, so writes are
 *  last-writer-wins. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, FormControlLabel, MenuItem, Switch, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  MIN_PREFIX_LEN,
  create as createAlias,
  update as updateAlias,
  type TagNameAlias,
  type TagNameAliasWrite,
} from '@/api/tagNameAliases';
import { AppDialog } from '@/components/AppDialog';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { usePermissions } from '@/hooks/usePermissions';
import { CANONICAL_TOKEN_HINT, CANONICAL_TOKEN_RE } from '@/utils/canonicalToken';
import { singlePageTenant } from '@/scope/pageTenants';

import { TAG_NAME_ALIAS_HELP } from './fieldHelp';

const FormSchema = z
  .object({
    category_code: z.string().trim().min(1, 'Category code is required').max(64),
    alias: z.string().trim().min(1, 'Alias is required').max(1024),
    canonical: z
      .string()
      .trim()
      .min(1, 'Canonical token is required')
      .max(128)
      .refine((v) => CANONICAL_TOKEN_RE.test(v.toLowerCase()), CANONICAL_TOKEN_HINT),
    match_mode: z.enum(['exact', 'prefix']),
    enabled: z.boolean(),
  })
  // Mirrors the backend floor rather than trusting it: a two-character prefix in
  // a category with thousands of names matches an arbitrary share of them, and
  // catching it here names the offending field instead of surfacing a 400.
  .refine((v) => v.match_mode !== 'prefix' || v.alias.trim().length >= MIN_PREFIX_LEN, {
    path: ['alias'],
    message: `A prefix alias needs at least ${MIN_PREFIX_LEN} characters — a shorter one matches far more tags than intended.`,
  });

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = {
  category_code: '',
  alias: '',
  canonical: '',
  match_mode: 'exact',
  enabled: true,
};

function toDefaults(alias: TagNameAlias | null): FormValues {
  if (!alias) return EMPTY;
  return {
    category_code: alias.category_code,
    alias: alias.alias,
    canonical: alias.canonical,
    match_mode: alias.match_mode,
    enabled: alias.enabled,
  };
}

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  alias: TagNameAlias | null;
  /** The page's operators (D9), for create: the one is pre-selected, all are
   *  featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  /** Preselected category on create (from the grid's category filter). */
  defaultCategory?: string;
  onClose: () => void;
}

export function TagNameAliasFormDialog({
  open,
  alias,
  pageTenants,
  defaultCategory,
  onClose,
}: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = alias !== null;
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.tag_name_alias',
    objectPk: alias?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['tag_name_aliases'] }),
  });

  // Tenant lives outside RHF (self-contained controlled input). null = system-wide.
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(alias),
  });

  useEffect(() => {
    if (!open) return;
    history.reset();
    reset(alias ? toDefaults(alias) : { ...EMPTY, category_code: defaultCategory ?? '' });
    if (alias) {
      setTenant(
        alias.tenant
          ? { id: alias.tenant.id, name: alias.tenant.name, code: alias.tenant.code }
          : null,
      );
    } else {
      setTenant(singlePageTenant(pageTenants));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, alias?.id]);

  const mutation = useMutation({
    mutationFn: (body: TagNameAliasWrite) =>
      isEdit ? updateAlias(alias.id, body) : createAlias(body),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Tag name alias updated.' : 'Tag name alias created.', 'success');
      qc.invalidateQueries({ queryKey: ['tag_name_aliases'] });
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    mutation.mutate({
      tour_operator: tenant?.id ?? null,
      category_code: v.category_code.trim(),
      alias: v.alias.trim(),
      canonical: v.canonical.trim().toLowerCase(),
      match_mode: v.match_mode,
      enabled: v.enabled,
    });
  });

  const matchMode = watch('match_mode');

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title={
        <>
          {isEdit ? `Edit tag name alias · ${alias.alias}` : 'New tag name alias'}{' '}
          <HelpLink story="tag-name-aliases" newTab />
        </>
      }
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create alias'}
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
        <Box
          component="form"
          onSubmit={submit}
          noValidate
          sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField
            {...register('category_code')}
            label="Category code"
            size="small"
            fullWidth
            error={Boolean(errors.category_code)}
            helperText={
              errors.category_code?.message ?? 'The tag category this alias applies within.'
            }
            InputProps={{ endAdornment: <FieldHelp help={TAG_NAME_ALIAS_HELP.category} /> }}
          />

          <TextField
            {...register('alias')}
            label="Alias"
            size="small"
            fullWidth
            error={Boolean(errors.alias)}
            helperText={
              errors.alias?.message ??
              (matchMode === 'prefix'
                ? 'Matches any tag name starting with this.'
                : 'A tag name as it appears in the data (matched case-insensitively).')
            }
            InputProps={{ endAdornment: <FieldHelp help={TAG_NAME_ALIAS_HELP.alias} /> }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Controller
              control={control}
              name="match_mode"
              render={({ field }) => (
                <TextField
                  select
                  label="Match mode"
                  size="small"
                  fullWidth
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  <MenuItem value="exact">Exact name</MenuItem>
                  <MenuItem value="prefix">Name starts with</MenuItem>
                </TextField>
              )}
            />
            <FieldHelp help={TAG_NAME_ALIAS_HELP.matchMode} />
          </Box>

          <TextField
            {...register('canonical')}
            label="Canonical token"
            size="small"
            fullWidth
            error={Boolean(errors.canonical)}
            helperText={
              errors.canonical?.message ?? 'The governed token conditions reference, e.g. "viator".'
            }
            InputProps={{ endAdornment: <FieldHelp help={TAG_NAME_ALIAS_HELP.canonical} /> }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ flexGrow: 1 }}>
              <TenantPicker
                value={tenant}
                featured={isEdit ? undefined : pageTenants}
                onChange={setTenant}
                label="Operator"
                placeholder="System / global (no operator)"
              />
            </Box>
            <FieldHelp help={TAG_NAME_ALIAS_HELP.operator} />
          </Box>

          <Controller
            control={control}
            name="enabled"
            render={({ field }) => (
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={labelWithHelp('Enabled', TAG_NAME_ALIAS_HELP.enabled)}
              />
            )}
          />
        </Box>
      )}
    </AppDialog>
  );
}
